// Các phép tính trên cây tổ chức (phòng ban / nhân sự) — tách riêng để test và dùng lại khi có API.

export interface RawNode {
  id: string;
  type: 'department' | 'employee';
  /** Hai cấp cố định trên cùng; phòng ban thường không có */
  kind?: 'company' | 'board';
  label?: string;
  count?: number;
  badgeColor?: string;
  name?: string;
  role?: string;
  avatar?: string;
  /** id hồ sơ nhân viên (staffs); dữ liệu mẫu chưa gắn hồ sơ thì không có */
  staffId?: number;
  children?: RawNode[];
}

export interface EmployeeRef { node: RawNode; path: string[] }

export const norm = (s: string) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase().trim();

/** Tổng số nhân sự trong cả nhánh (kể cả phòng ban con) */
export function countEmployees(node: RawNode): number {
  if (node.type === 'employee') return 1 + (node.children || []).reduce((s, c) => s + countEmployees(c), 0);
  return (node.children || []).reduce((s, c) => s + countEmployees(c), 0);
}

/** Mọi nhân sự trong nhánh kèm đường dẫn phòng ban (để hiện "Ban Giám Đốc › Phòng BA") */
export function collectEmployees(node: RawNode, path: string[] = []): EmployeeRef[] {
  const here = node.type === 'department' ? [...path, node.label || ''] : path;
  const self = node.type === 'employee' ? [{ node, path }] : [];
  return [...self, ...(node.children || []).flatMap(c => collectEmployees(c, here))];
}

/** Chức danh quản lý: trưởng / phó / giám đốc / quản lý / leader */
export const isLeader = (role?: string) => /\btruong\b|\bpho\b|giam doc|quan ly|\bleader\b|\blead\b|\bhead\b|manager|director|\btong\b/.test(norm(role || ''));

/** Chỉ giữ nhân sự là quản lý; phòng ban không còn ai thì bỏ khỏi cây (trừ gốc). Quản lý dưới một nhân viên được đẩy lên thay chỗ người đó */
export function filterLeaders(node: RawNode, isRoot = true): RawNode | null {
  const pick = (n: RawNode): RawNode[] => {
    const children = (n.children || []).flatMap(pick);
    if (n.type === 'employee') return isLeader(n.role) ? [{ ...n, children }] : children;
    return children.length ? [{ ...n, children }] : [];
  };
  const children = (node.children || []).flatMap(pick);
  if (!children.length && !isRoot) return null;
  return { ...node, children };
}

/** Cấp chức vụ để nhóm "Theo chức vụ" */
export function roleLevel(role?: string): 0 | 1 | 2 {
  const r = norm(role || '');
  if (/giam doc|tong|director|ceo|chu tich/.test(r)) return 0;
  if (isLeader(role)) return 1;
  return 2;
}
export const LEVEL_LABEL = ['Ban lãnh đạo', 'Quản lý', 'Nhân viên'] as const;
const LEVEL_COLOR = ['bg-amber-500', 'bg-blue-500', 'bg-emerald-500'];

/** Cây theo chức vụ: gốc → 3 cấp (lãnh đạo / quản lý / nhân viên) → nhân sự (chức danh kèm phòng ban) */
export function buildPositionTree(root: RawNode, companyLabel: string): RawNode {
  const groups: RawNode[][] = [[], [], []];
  collectEmployees(root).forEach(({ node, path }) => {
    const dept = path[path.length - 1];
    groups[roleLevel(node.role)].push({ ...node, id: `pos-${node.id}`, role: [node.role, dept].filter(Boolean).join(' · '), children: undefined });
  });
  return {
    id: 'pos-root', type: 'department', label: companyLabel, badgeColor: 'bg-amber-500',
    children: groups
      .map((list, i) => ({ id: `pos-level-${i}`, type: 'department' as const, label: LEVEL_LABEL[i], badgeColor: LEVEL_COLOR[i], children: list }))
      .filter(g => g.children.length),
  };
}

export interface SearchHit { id: string; kind: 'department' | 'employee'; label: string; sub: string; depth: number }

/** Tìm phòng ban và nhân sự (không dấu); rỗng = liệt kê phòng ban theo cây */
export function searchOrg(root: RawNode, q: string): SearchHit[] {
  const k = norm(q);
  const out: SearchHit[] = [];
  const walk = (n: RawNode, depth: number, path: string[]) => {
    if (n.type === 'department') {
      if (!k || norm(n.label || '').includes(k)) out.push({ id: n.id, kind: 'department', label: n.label || '', sub: `${countEmployees(n)} nhân sự`, depth });
      (n.children || []).forEach(c => walk(c, depth + 1, [...path, n.label || '']));
    } else {
      if (k && (norm(n.name || '').includes(k) || norm(n.role || '').includes(k))) {
        out.push({ id: n.id, kind: 'employee', label: n.name || '', sub: [n.role, path[path.length - 1]].filter(Boolean).join(' · '), depth });
      }
      (n.children || []).forEach(c => walk(c, depth + 1, path));
    }
  };
  walk(root, 0, []);
  return out;
}

export function getAncestorsAndSelf(root: RawNode, targetId: string, path: string[] = []): string[] | null {
  const currentPath = [...path, root.id];
  if (root.id === targetId) return currentPath;
  for (const child of root.children || []) {
    const res = getAncestorsAndSelf(child, targetId, currentPath);
    if (res) return res;
  }
  return null;
}

export function findNode(root: RawNode, id: string): RawNode | null {
  if (root.id === id) return root;
  for (const c of root.children || []) { const f = findNode(c, id); if (f) return f; }
  return null;
}

export function parentOf(root: RawNode, id: string): RawNode | null {
  for (const c of root.children || []) {
    if (c.id === id) return root;
    const f = parentOf(c, id);
    if (f) return f;
  }
  return null;
}

/** Công ty chỉ có Ban lãnh đạo; Ban lãnh đạo và phòng ban chứa được phòng ban, nhân sự; nhân sự chỉ có cấp dưới là nhân sự */
export function canHold(parent: RawNode, type: RawNode['type']): boolean {
  if (parent.kind === 'company') return false;
  return parent.type === 'department' || type === 'employee';
}

/** Chức danh gợi ý khi thêm: phòng chưa có trưởng → Trưởng phòng, dưới Trưởng phòng → Lead, còn lại → Nhân viên */
export function suggestRole(parent: RawNode): string {
  if (parent.kind === 'board') return '';
  if (parent.type === 'employee') return /truong/.test(norm(parent.role || '')) ? 'Lead' : 'Nhân viên';
  return (parent.children || []).some(c => c.type === 'employee' && isLeader(c.role)) ? 'Nhân viên' : 'Trưởng phòng';
}

export interface ParentOption { value: string; title: string; disabled: boolean; children: ParentOption[] }

/** Cây chọn "Thuộc" cho TreeSelect, bỏ chính nút đang sửa và nhánh của nó */
export function parentOptions(root: RawNode, type: RawNode['type'], selfId?: string): ParentOption[] {
  const walk = (n: RawNode): ParentOption[] => {
    if (n.id === selfId || (n.type === 'employee' && type === 'department')) return [];
    return [{
      value: n.id,
      title: n.type === 'employee' ? `${n.name}${n.role ? ` (${n.role})` : ''}` : n.label || '',
      disabled: !canHold(n, type),
      children: (n.children || []).flatMap(walk),
    }];
  };
  return walk(root);
}

export interface OrgDraft { id?: string; type: RawNode['type']; parentId?: string; label?: string; name?: string; role?: string; staffId?: number; avatar?: string }
export type OrgResult = { ok: true; tree: RawNode; id: string } | { ok: false; error: string };

const mapNode = (n: RawNode, id: string, fn: (x: RawNode) => RawNode): RawNode =>
  n.id === id ? fn(n) : n.children ? { ...n, children: n.children.map(c => mapNode(c, id, fn)) } : n;

const withChildren = (n: RawNode, children: RawNode[]): RawNode => ({ ...n, children });

const nameTaken = (parent: RawNode, label: string, selfId?: string) =>
  (parent.children || []).some(c => c.type === 'department' && c.id !== selfId && norm(c.label || '') === norm(label));

/** Hồ sơ đã nằm trên sơ đồ, trừ mục đang sửa: mỗi người chỉ có một chỗ */
export function staffIdsOnChart(root: RawNode, selfId?: string): number[] {
  return collectEmployees(root).filter(e => e.node.id !== selfId && e.node.staffId != null).map(e => e.node.staffId as number);
}

const newId = (type: RawNode['type']) => `${type === 'department' ? 'dept' : 'emp'}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Thêm mới (không có id) hoặc sửa, kể cả chuyển sang đơn vị khác */
export function saveNode(root: RawNode, d: OrgDraft): OrgResult {
  const label = (d.label || '').trim();
  const name = (d.name || '').trim();
  const role = (d.role || '').trim();
  const fields = d.type === 'department' ? { label } : { name, role, ...(d.staffId != null ? { staffId: d.staffId, avatar: d.avatar } : {}) };
  if (d.type === 'department' && !label) return { ok: false, error: 'Nhập tên phòng ban' };
  if (d.type === 'employee' && !name) return { ok: false, error: 'Nhập họ tên' };
  if (d.type === 'employee' && !role) return { ok: false, error: 'Nhập chức danh' };
  if (d.staffId != null && staffIdsOnChart(root, d.id).includes(d.staffId)) return { ok: false, error: `${name} đã có trên sơ đồ` };

  const self = d.id ? findNode(root, d.id) : null;
  if (d.id && !self) return { ok: false, error: 'Không còn tìm thấy mục này' };

  // Công ty và Ban lãnh đạo chỉ đổi được tên, không chuyển chỗ
  if (self?.kind) {
    const up = parentOf(root, self.id);
    if (up && nameTaken(up, label, self.id)) return { ok: false, error: 'Đã có đơn vị cùng tên' };
    return { ok: true, tree: mapNode(root, self.id, n => ({ ...n, label })), id: self.id };
  }

  const parent = d.parentId ? findNode(root, d.parentId) : null;
  if (!parent) return { ok: false, error: 'Chọn đơn vị trực thuộc' };
  if (self && (getAncestorsAndSelf(self, parent.id))) return { ok: false, error: 'Không chuyển được vào chính nhánh của mình' };
  if (!canHold(parent, d.type)) return { ok: false, error: `${parent.label || parent.name} không chứa được mục này` };
  if (d.type === 'department' && nameTaken(parent, label, self?.id)) return { ok: false, error: `${parent.label} đã có phòng ban "${label}"` };

  if (!self) {
    const id = newId(d.type);
    const node: RawNode = { id, type: d.type, ...fields, ...(d.type === 'department' ? { badgeColor: 'bg-amber-500' } : {}), children: [] };
    return { ok: true, tree: mapNode(root, parent.id, n => withChildren(n, [...(n.children || []), node])), id };
  }

  const edited = { ...self, ...fields };
  const from = parentOf(root, self.id)!;
  if (from.id === parent.id) return { ok: true, tree: mapNode(root, self.id, () => edited), id: self.id };
  const detached = mapNode(root, from.id, n => withChildren(n, (n.children || []).filter(c => c.id !== self.id)));
  return { ok: true, tree: mapNode(detached, parent.id, n => withChildren(n, [...(n.children || []), edited])), id: self.id };
}

/**
 * Xóa một mục, trả về id cha để chọn lại trên sơ đồ.
 * Nhân sự: cấp dưới chuyển lên người (đơn vị) phía trên. Phòng ban còn nội dung: phải chọn nơi chuyển đến.
 */
export function deleteNode(root: RawNode, id: string, moveToId?: string): OrgResult {
  const self = findNode(root, id);
  if (!self) return { ok: false, error: 'Không còn tìm thấy mục này' };
  if (self.kind) return { ok: false, error: `Không xóa được ${self.label}` };
  const parent = parentOf(root, id)!;
  const kids = self.children || [];

  if (self.type === 'employee' || !kids.length) {
    const tree = mapNode(root, parent.id, n => withChildren(n, (n.children || []).flatMap(c => (c.id === id ? kids : [c]))));
    return { ok: true, tree, id: parent.id };
  }

  const target = moveToId ? findNode(root, moveToId) : null;
  if (!target) return { ok: false, error: 'Chọn nơi chuyển nhân sự đến' };
  if (getAncestorsAndSelf(self, target.id)) return { ok: false, error: 'Nơi chuyển đến đang nằm trong phòng ban bị xóa' };
  if (target.type !== 'department' || !canHold(target, 'department')) return { ok: false, error: `Không chuyển được vào ${target.label || target.name}` };
  const clash = kids.find(k => k.type === 'department' && nameTaken(target, k.label || ''));
  if (clash) return { ok: false, error: `${target.label} đã có phòng ban "${clash.label}"` };

  const removed = mapNode(root, parent.id, n => withChildren(n, (n.children || []).filter(c => c.id !== id)));
  return { ok: true, tree: mapNode(removed, target.id, n => withChildren(n, [...(n.children || []), ...kids])), id: parent.id };
}

export interface NodeRect { id: string; x: number; y: number; w: number; h: number }
export type DropPlan =
  | { mode: 'into'; targetId: string }
  | { mode: 'order'; beforeId: string | null; lineX: number; lineY: number };

/**
 * Kéo phòng ban: thả trúng phòng ban khác là chuyển vào đó, thả chỗ trống là đổi thứ tự trong các phòng cùng cấp.
 * Trả về null nếu thả xong không có gì thay đổi.
 */
export function planDrop(root: RawNode, id: string, pointer: { x: number; y: number }, rects: NodeRect[]): DropPlan | null {
  const self = findNode(root, id);
  const parent = parentOf(root, id);
  if (!self || !parent || self.kind || self.type !== 'department') return null;

  const hit = rects.find(r => {
    if (r.id === parent.id || getAncestorsAndSelf(self, r.id)) return false;
    if (pointer.x < r.x || pointer.x > r.x + r.w || pointer.y < r.y || pointer.y > r.y + r.h) return false;
    const target = findNode(root, r.id);
    return !!target && target.type === 'department' && canHold(target, 'department');
  });
  if (hit) return { mode: 'into', targetId: hit.id };

  const depts = (parent.children || []).filter(c => c.type === 'department');
  const others = depts.filter(c => c.id !== id).map(c => rects.find(r => r.id === c.id)).filter((r): r is NodeRect => !!r);
  if (!others.length) return null;
  const at = others.findIndex(r => pointer.y < r.y + r.h / 2);
  const before = at >= 0 ? others[at] : null;
  const beforeId = before ? before.id : null;
  if (beforeId === (depts[depts.indexOf(self) + 1]?.id ?? null)) return null;

  const last = others[others.length - 1];
  const prev = at > 0 ? others[at - 1] : null;
  const lineY = !before ? last.y + last.h + 8 : prev ? (prev.y + prev.h + before.y) / 2 : before.y - 8;
  return { mode: 'order', beforeId, lineX: (before || last).x, lineY };
}

/** Đưa phòng ban lên trước một phòng cùng cấp (beforeId null: xuống cuối) */
export function reorderDept(root: RawNode, id: string, beforeId: string | null): RawNode {
  const parent = parentOf(root, id);
  const self = findNode(root, id);
  if (!parent || !self) return root;
  const kids = (parent.children || []).filter(c => c.id !== id);
  const at = beforeId ? kids.findIndex(c => c.id === beforeId) : -1;
  kids.splice(at >= 0 ? at : kids.length, 0, self);
  return mapNode(root, parent.id, n => withChildren(n, kids));
}
